<script>
import { getComponentsNames } from '@/v-shop/autoloader'

export default {
	lang: 'shop',
	inject: ['getAppMessageDialog'],
	props: {
		order: Object,
		loading: Boolean,
		firstLoading: Boolean,
	},
	data() {
		return {
			loadingDownload: false,
			surveyLaunched: false,
		}
	},
	computed: {
		orderDetailComps() {
			return getComponentsNames().filter((name) => name.startsWith('UserOrderDetail-Custom-'))
		},
		isPaid() {
			return this.order.paymentStatus == 'paid'
		},
		showPaymentDetails() {
			return this.order.paymentStatus != 'canceled' && !this.order.isSimple
		},
		showDeliveryDetails() {
			return this.order.deliveries.some((x) => x.status != 'canceled') && !this.order.isSimple
		},
		showDownloadFiles() {
			return this.order.dataBySeller.some((sellerData) => sellerData.invoiceFile)
		},
		canPay() {
			return this.order.paymentStatus == 'pending'
		},
	},
	methods: {
		async downloadInvoiceFile(sellerData) {
			this.loadingDownload = true

			const basePath = this.$route.query.hash ? `/guest-order` : `/user/order`
			const response = await fetch(
				this.$shopApi.makeUrl(
					`${basePath}/${this.$route.query.hash}/download-invoice-file/${sellerData.seller.id}`,
					{
						query: this.$route.query.hash ? { hash: this.$route.query.hash } : undefined,
					}
				)
			)

			if (response.ok) {
				const blob = await response.blob()
				const blobURL = URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.setAttribute('href', blobURL)
				link.setAttribute('download', sellerData.invoiceFile)
				link.style.display = 'none'
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
			} else {
				this.getAppMessageDialog().open({
					title: 'Error al descargar la factura',
					text: 'No se pudo descargar la factura, por favor contacta al vendedor para obtenerla.',
					dialogId: 'error-download-invoice',
				})
			}

			this.loadingDownload = false
		},
	},
}
</script>

<template>
	<div v-if="loading && firstLoading" class="d-flex align-center justify-center py-16">
		<v-progress-circular size="50" indeterminate />
		<div class="ml-4">{{ 'Cargando el detalle de la compra' | lang }}</div>
	</div>
	<div v-else-if="order" :class="{ disabled: loading && !firstLoading }">
		<CardLayout :color="order.statusesInfo.title.color" class="mb-6">
			<template #title>
				<div class="px-2 rounded" :class="`${order.statusesInfo.title.color} white--text`">
					{{ order.statusesInfo.title.buyerName || order.statusesInfo.title.name }}
				</div>
				<v-spacer />
				<v-menu v-if="showDownloadFiles" offset-y>
					<template v-slot:activator="{ on, attrs }">
						<Button
							color="link"
							small
							text
							v-on="on"
							v-bind="attrs"
							:disabled="loadingDownload"
							:loading="loadingDownload"
						>
							<v-icon left>mdi-download</v-icon>
							{{ $lang('Descargar Facturas') }}
						</Button>
					</template>
					<v-list dense>
						<v-list-item
							v-for="sellerData of order.dataBySeller"
							:key="sellerData.seller.id"
							@click="downloadInvoiceFile(sellerData)"
							dense
						>
							<v-list-item-icon>
								<v-icon>mdi-file-download</v-icon>
							</v-list-item-icon>
							<v-list-item-title>{{ sellerData.seller.name }}</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</template>
			<div>
				<div class="d-flex align-center">
					<div class="d-flex align-center">
						<v-icon class="mr-1">mdi-shopping</v-icon>
						<b>Compra #{{ order.code }}</b>
					</div>
					<div class="ml-4 d-flex align-center">
						<v-icon class="mr-1">mdi-clock</v-icon>
						{{ order.statusesAt.main.confirmed | datetime }}
					</div>
				</div>
			</div>
		</CardLayout>
		<template v-if="showPaymentDetails">
			<!-- discount coupon -->
			<UserOrderDetail-Discount :order="order" class="mb-6" />
			<!-- payment -->
			<v-row no-gutters>
				<v-col cols="12" sm="7">
					<!-- payment detail -->
					<UserOrderDetail-Payment :order="order" class="mb-6 mr-sm-6" />
				</v-col>
				<v-col cols="12" sm="5">
					<!-- contact data -->
					<UserOrderDetail-Contact :order="order" class="mb-6" />
				</v-col>
			</v-row>

			<!-- if can pay: payment instructions -->
			<UserOrderDetail-PaymentInstructions :order="order" v-if="canPay" class="mb-6" />

			<!-- digital products -->
			<UserOrderDetail-DigitalItems :order="order" v-if="isPaid" class="mb-6" />

			<!-- delivery -->
			<template v-if="showDeliveryDetails">
				<UserOrderDetail-Delivery
					v-for="(delivery, i) of order.deliveries"
					:key="i"
					:order="order"
					:delivery="delivery"
					class="mb-6"
				/>
			</template>
		</template>

		<component
			v-for="compName of orderDetailComps"
			:key="compName"
			:is="compName"
			v-bind="{ order, showPaymentDetails, showDeliveryDetails }"
		/>

		<!-- summary -->
		<OrderSummary :order="order" />
	</div>
</template>

<style scoped>
.disabled {
	opacity: 0.5;
}
.disabled,
.disabled ::v-deep * {
	pointer-events: none !important;
}
</style>

